// pages/AIDataSources.js — Kelola data yang di-inject ke AI per dashboard.
// Permission: 'ai_config'. Query bersifat SELECT-only (divalidasi di backend).
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useTabDeepLink from '../hooks/useTabDeepLink';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { DASHBOARD_KEYS } from '../config/dashboardKeys';
import {
  Database, Plus, Edit2, Trash2, Save, X, Play, RefreshCw,
  CheckCircle2, AlertTriangle, Eye, Power, ChevronDown,
} from 'lucide-react';

const DASHBOARDS = [
  { key: 'solman',      label: 'SAP Solman',         table: 'solman_tickets' },
  { key: 'aws_billing', label: 'AWS Billing',        table: 'aws_billing_raw' },
  { key: 'aws_cost',    label: 'AWS Cost Explorer',  table: 'aws_cost_raw' },
  { key: 'sap_usage',   label: 'SAP User Usage',     table: 'sap_usage' },
  { key: 'employee_overtime', label: 'Employee Overtime', table: 'employee_overtime' },
];

// Key kanonik untuk SQL Mode berasal dari sumber tunggal (config/dashboardKeys).
// Menambah dashboard baru cukup di file itu — dropdown ini otomatis ikut.
const SQL_DASH_KEYS = DASHBOARD_KEYS;

// ── Kamus alias: konversi JSON (disimpan di ai_sql_sources.aliases) ⇄ teks ──
// Format editor: satu baris per pasangan `singkatan = Nama Kanonik di DB`.
// Satu singkatan boleh diulang di beberapa baris untuk memetakan ke banyak
// nama (mis. "pmi" ambigu). Dipush ke AI Service sbg {alias_map} di prompt
// SQL-gen agar singkatan user ("bio", "pmi") cocok via LIKE, bukan '=' kosong.
function aliasesToText(jsonStr) {
  try {
    const arr = JSON.parse(jsonStr || '[]');
    if (!Array.isArray(arr)) return '';
    return arr
      .filter(a => a && a.alias && a.canonical)
      .map(a => `${a.alias} = ${a.canonical}`)
      .join('\n');
  } catch (_) { return ''; }
}
function textToAliases(text) {
  return String(text || '')
    .split('\n')
    .map(line => {
      const i = line.indexOf('=');
      if (i < 1) return null;                    // baris tanpa '=' dilewati
      const alias = line.slice(0, i).trim();
      const canonical = line.slice(i + 1).trim(); // '=' berikutnya ikut canonical
      return alias && canonical ? { alias, canonical } : null;
    })
    .filter(Boolean);
}

// Ambil template per-mode dari string JSON tersimpan → { sql_gen, summarize }.
function tplFromJson(jsonStr) {
  try {
    const o = JSON.parse(jsonStr || '{}');
    return (o && typeof o === 'object' && !Array.isArray(o)) ? o : {};
  } catch (_) { return {}; }
}

// ── Editor modal (tambah / edit satu data source) ────────────────────────────
function QueryEditor({ dashboardKey, initial, onClose, onSaved, showToast }) {
  const { t } = useLang();
  const isEdit = !!initial?.id;
  const [label, setLabel]       = useState(initial?.label || '');
  const [sql, setSql]           = useState(initial?.sql_query || '');
  const [enabled, setEnabled]   = useState(initial ? !!initial.enabled : true);
  const [botKey, setBotKey]     = useState(initial?.bot_key || '');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  // Preview state
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]       = useState(null); // { rows, rowCount, rendered }
  const [previewErr, setPreviewErr] = useState(null);

  const runPreview = async () => {
    setPreviewing(true); setPreviewErr(null); setPreview(null);
    try {
      const res = await api.post('/api/ai/context-queries/preview', { sql_query: sql, label: label || 'Preview' });
      setPreview(res.data);
    } catch (e) {
      setPreviewErr(e.response?.data?.error || e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!label.trim() || !sql.trim()) { setError(t('ads.errLabelSql')); return; }
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await api.put(`/api/ai/context-queries/${initial.id}`, { label, sql_query: sql, enabled, bot_key: botKey });
      } else {
        await api.post('/api/ai/context-queries', { dashboard_key: dashboardKey, label, sql_query: sql, enabled, bot_key: botKey });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">
            {isEdit ? t('ads.editTitle') : t('ads.addTitle')}
          </h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X style={{ width: 16, height: 16 }} className="text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{t('ads.label')}</label>
            <input type="text" value={label} onChange={e => setLabel(e.target.value)}
              placeholder={t('ads.labelPlaceholder')}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{t('ads.sqlLabel')}</label>
            <textarea value={sql} onChange={e => setSql(e.target.value)} rows={6}
              placeholder={t('ads.sqlPlaceholder')}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-mono outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 resize-none text-slate-700" />
            <p className="text-xs text-slate-400 mt-1">
              {t('ads.sqlNotePre')} <strong>SELECT</strong>/<strong>WITH</strong>. {t('ads.sqlNotePost')}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setEnabled(e => !e)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
              <Power style={{ width: 12, height: 12 }} />
              {enabled ? t('ads.active') : t('ads.inactive')}
            </button>

            {/* Assign ke Bot Telegram */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-500 whitespace-nowrap">{t('ads.botTelegram')}</span>
              <select value={botKey} onChange={e => setBotKey(e.target.value)}
                className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-700 bg-white focus:outline-none focus:border-brand-400">
                <option value="">{t('ads.botNone')}</option>
                <option value="billing">{t('ads.botBilling')}</option>
                <option value="cost">{t('ads.botCost')}</option>
                <option value="solman">{t('ads.botSolman')}</option>
                <option value="sap_usage">{t('ads.botUsage')}</option>
                <option value="notify">{t('ads.botNotify')}</option>
              </select>
            </div>

            <button onClick={runPreview} disabled={previewing || !sql.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-600 border border-brand-200 hover:border-brand-300 transition-colors disabled:opacity-50">
              {previewing
                ? <RefreshCw style={{ width: 12, height: 12 }} className="animate-spin" />
                : <Play style={{ width: 12, height: 12 }} />}
              {t('ads.preview')}
            </button>
          </div>

          {previewErr && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 flex items-start gap-1.5">
              <AlertTriangle style={{ width: 13, height: 13 }} className="mt-0.5 flex-shrink-0" />
              {previewErr}
            </div>
          )}

          {preview && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                <Eye style={{ width: 12, height: 12 }} />
                {t('ads.rowCount', { n: preview.rowCount })} {preview.rowCount > 50 && t('ads.first50')}
              </div>
              {preview.rows && preview.rows.length > 0 ? (
                <div className="overflow-x-auto max-h-52">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        {Object.keys(preview.rows[0]).map(c => (
                          <th key={c} className="px-3 py-1.5 text-left font-semibold text-slate-500 whitespace-nowrap">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {Object.keys(preview.rows[0]).map(c => (
                            <td key={c} className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{String(r[c] ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-3 py-4 text-xs text-slate-400 text-center">{t('ads.previewNoData')}</div>
              )}
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">{error}</div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex gap-2">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all">
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 px-4 rounded-xl text-sm font-semibold bg-brand-600 hover:bg-brand-700 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {saving
              ? <><RefreshCw style={{ width: 14, height: 14 }} className="animate-spin" /> {t('ads.saving')}</>
              : <><Save style={{ width: 14, height: 14 }} /> {isEdit ? t('ads.saveChanges') : t('ads.add')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Halaman utama ────────────────────────────────────────────────────────────
// ── Panel Mode SQL (text-to-SQL) — kelola dashboard mana yang pakai SQL dinamis ─
function SqlSourcesManager({ showToast, pg = false }) {
  // [MR-PG-AI] pg=true → source PostgreSQL Management Reports (tabel ai_pg_sources).
  const EP = pg ? { list: '/api/ai/pg-sources', tables: '/api/ai/pg-tables' } : { list: '/api/ai/sql-sources', tables: '/api/ai/db-tables' };
  const { t } = useLang();
  const [sources, setSources] = useState([]);
  const [tables, setTables]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { dashboard_key, tables:[], hint, enabled, isNew }
  const [tplDefaults, setTplDefaults] = useState(null); // cache DEFAULT_TEMPLATES dari AI Service

  // Muat template default ke field override (edit dari bawaan, bukan dari kosong).
  const loadDefaultInto = async (mode, field) => {
    try {
      let d = tplDefaults;
      if (!d) {
        const r = await api.get('/api/ai/prompt-defaults');
        d = r.data?.defaults || {};
        setTplDefaults(d);
      }
      const tpl = d[mode];
      if (!tpl) { showToast('Default template is not available.', 'error'); return; }
      if ((editing?.[field] || '').trim() && !window.confirm('Replace the current override with the built-in default template?')) return;
      setEditing(prev => ({ ...prev, [field]: tpl }));
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to load the default template.', 'error');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(EP.list),
        api.get(EP.tables).catch(err => { if (pg) { showToast(err.response?.data?.error || 'PostgreSQL MR tidak terjangkau', 'error'); return { data: [] }; } throw err; }),
      ]);
      setSources(s.data || []);
      setTables(t.data || []);
    } catch (e) {
      showToast(e.response?.data?.error || t('ads.errLoadSql'), 'error');
    } finally { setLoading(false); }
  }, [showToast, pg]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const cfg = editing;
    if (!cfg.dashboard_key.trim()) { showToast(t('ads.errDashKey'), 'error'); return; }
    if (!cfg.tables.length)        { showToast(t('ads.errPickTable'), 'error'); return; }
    try {
      await api.post(EP.list, {
        dashboard_key: cfg.dashboard_key.trim(),
        ...(pg ? { label: (cfg.label || '').trim() } : {}),
        tables: cfg.tables, hint: cfg.hint, few_shot: cfg.few_shot || '',
        aliases: textToAliases(cfg.aliasText),
        prompt_templates: {
          sql_gen:   (cfg.tplSqlGen || '').trim(),
          summarize: (cfg.tplSummarize || '').trim(),
        },
        enabled: cfg.enabled ? 1 : 0,
      });
      showToast(t('ads.sqlSaved'));
      setEditing(null); load();
    } catch (e) { showToast(e.response?.data?.error || t('ads.errSave'), 'error'); }
  };

  const remove = async (dk) => {
    if (!window.confirm(t('ads.confirmRemoveSql', { dk }))) return;
    try {
      await api.delete(`${EP.list}/${encodeURIComponent(dk)}`);
      showToast(t('ads.removed')); load();
    } catch (e) { showToast(e.response?.data?.error || t('ads.errRemove'), 'error'); }
  };

  const toggleTable = (name) => {
    setEditing(prev => {
      const has = prev.tables.includes(name);
      return { ...prev, tables: has ? prev.tables.filter(t => t !== name) : [...prev.tables, name] };
    });
  };

  return (
    <div>
      {pg ? (
        <div className="mb-4 px-4 py-3 bg-sky-50 border border-sky-100 rounded-xl text-xs text-sky-800 leading-relaxed">
          <strong>PostgreSQL · Management Reports.</strong> Data dibaca langsung dari database SAP_ALL di server Management Reports
          (read-only, lewat tunnel). Source di sini muncul di AI Workspace sebagai "MR · …". Simpan = langsung didaftarkan ulang ke AI Service.
          Aturan pengaman (filter kategori maintenance SP/CO/SV, pembatasan company untuk user MR, fungsi SQL yang diblok) tetap di kode dan tidak bisa diubah dari sini.
        </div>
      ) : (
      <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700 leading-relaxed">
        <strong>{t('ads.sqlModeInfoBold')}</strong> {t('ads.sqlModeInfoRest')}
      </div>
      )}

      <div className="flex justify-end gap-2 mb-4">
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all">
          <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
          {t('common.refresh')}
        </button>
        <button onClick={() => setEditing({ dashboard_key: pg ? 'mr_' : '', label: '', tables: [], hint: '', few_shot: '', aliasText: '', tplSqlGen: '', tplSummarize: '', enabled: true, isNew: true })}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md">
          <Plus style={{ width: 15, height: 15 }} />
          {t('ads.addSqlDash')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
          <RefreshCw style={{ width: 18, height: 18 }} className="animate-spin" />
          <span className="text-sm">{t('common.loading')}</span>
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Database style={{ width: 32, height: 32 }} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t('ads.noSqlDash')}</p>
          <p className="text-xs mt-1">{t('ads.noSqlDashHint')}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {sources.map(s => (
            <div key={s.dashboard_key}
              className={`border rounded-xl px-4 py-3 transition-all ${s.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="font-semibold text-sm text-slate-800">{s.dashboard_key}</span>
                    {pg && s.label && <span className="text-xs text-slate-500">{s.label}</span>}
                    {s.enabled
                      ? <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs font-medium">{t('ads.active')}</span>
                      : <span className="px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded text-xs font-medium">{t('ads.inactive')}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {s.tables.map(t => (
                      <code key={t} className="px-1.5 py-0.5 bg-brand-50 text-brand-600 rounded text-xs font-mono">{t}</code>
                    ))}
                  </div>
                  {s.hint && <p className="text-xs text-slate-400 line-clamp-2">{s.hint}</p>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => { const _tpl = tplFromJson(s.prompt_templates); setEditing({ ...s, aliasText: aliasesToText(s.aliases), tplSqlGen: _tpl.sql_gen || '', tplSummarize: _tpl.summarize || '', isNew: false }); }} title={t('common.edit')}
                    className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
                    <Edit2 style={{ width: 15, height: 15 }} />
                  </button>
                  {!(pg && s.builtin) && <button onClick={() => remove(s.dashboard_key)} title={t('common.delete')}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 style={{ width: 15, height: 15 }} />
                  </button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor inline */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-display font-bold text-slate-800">
                {editing.isNew ? t('ads.addSqlDash') : t('ads.editSqlDash', { dk: editing.dashboard_key })}
              </h3>
              <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-slate-100 rounded-lg">
                <X style={{ width: 18, height: 18 }} className="text-slate-400" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">dashboard_key</label>
                {pg && editing.isNew ? (
                  <input value={editing.dashboard_key}
                    onChange={e => setEditing({ ...editing, dashboard_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    placeholder="mr_nama_source"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none font-mono" />
                ) : editing.isNew ? (
                  <select value={editing.dashboard_key}
                    onChange={e => setEditing({ ...editing, dashboard_key: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none font-mono bg-white cursor-pointer">
                    <option value="">— pilih dashboard —</option>
                    {SQL_DASH_KEYS.map(d => (
                      <option key={d.key} value={d.key}>{d.key} — {d.label}</option>
                    ))}
                  </select>
                ) : (
                  <input value={editing.dashboard_key} disabled
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 text-slate-400 outline-none font-mono" />
                )}
              </div>

              {pg && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Label (tampil di AI Workspace)</label>
                  <input value={editing.label || ''} onChange={e => setEditing({ ...editing, label: e.target.value })}
                    placeholder="MR · Nama Report"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none" />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  {t('ads.tablesLabel', { n: editing.tables.length })}
                </label>
                <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto border border-slate-100 rounded-xl p-2">
                  {tables.map(t => (
                    <button key={t} onClick={() => toggleTable(t)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-mono text-left transition-colors
                        ${editing.tables.includes(t) ? 'bg-brand-50 text-brand-700 border border-brand-200' : 'text-slate-500 hover:bg-slate-50 border border-transparent'}`}>
                      {editing.tables.includes(t) && <CheckCircle2 style={{ width: 12, height: 12 }} />}
                      <span className="truncate">{t}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('ads.hintLabel')}</label>
                <textarea value={editing.hint} rows={3}
                  onChange={e => setEditing({ ...editing, hint: e.target.value })}
                  placeholder={t('ads.hintPlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none resize-y" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t('ads.fewShotLabel')}</label>
                <textarea value={editing.few_shot || ''} rows={4}
                  onChange={e => setEditing({ ...editing, few_shot: e.target.value })}
                  placeholder={t('ads.fewShotPlaceholder')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none resize-y font-mono" />
                <p className="text-[11px] text-slate-400 mt-1">{t('ads.fewShotHelp')}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Kamus Alias {(() => { const n = textToAliases(editing.aliasText).length; return n ? `(${n} entri)` : ''; })()}
                </label>
                <textarea value={editing.aliasText || ''} rows={5}
                  onChange={e => setEditing({ ...editing, aliasText: e.target.value })}
                  placeholder={'bio = PT Pacific Bio Industry\npmi = PT. Pacific Medan Industri\npamin = PT Pacific Agro Mining'}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none resize-y font-mono" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Satu baris per alias: <code className="font-mono">singkatan = nama lengkap di database</code>.
                  Ulangi singkatan yang sama di beberapa baris bila memetakan ke lebih dari satu nama.
                  AI akan mencocokkan singkatan ini ke nilai di kolom nama (perusahaan/domain/lisensi)
                  saat user bertanya memakai singkatan.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-600">
                    Template Override — SQL Generation (optional)
                  </label>
                  <button type="button" onClick={() => loadDefaultInto('sql_gen', 'tplSqlGen')}
                    className="text-[11px] text-brand-600 hover:text-brand-700 hover:underline">
                    Load default
                  </button>
                </div>
                <textarea value={editing.tplSqlGen || ''} rows={5}
                  onChange={e => setEditing({ ...editing, tplSqlGen: e.target.value })}
                  placeholder={'Leave empty to use the built-in template. If filled, it MUST contain the placeholders {schema}, {tables}, {dialect} (optional: {dateRange}, {alias_map}, {fewshot}).'}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none resize-y font-mono" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Replaces the system prompt for the SQL generation stage of this dashboard. Empty = built-in default.
                  A template missing a required placeholder is rejected silently by the AI Service (the default stays in effect).
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-600">
                    Template Override — Result Summary (optional)
                  </label>
                  <button type="button" onClick={() => loadDefaultInto('summarize', 'tplSummarize')}
                    className="text-[11px] text-brand-600 hover:text-brand-700 hover:underline">
                    Load default
                  </button>
                </div>
                <textarea value={editing.tplSummarize || ''} rows={4}
                  onChange={e => setEditing({ ...editing, tplSummarize: e.target.value })}
                  placeholder={'Leave empty to use the built-in template. If filled, it MUST contain the placeholder {langHint} (optional: {dashboard}).'}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none resize-y font-mono" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Replaces the prompt for the result-summary stage. Currency formatting and chart instructions are still appended automatically.
                </p>
              </div>

              <button onClick={() => setEditing({ ...editing, enabled: !editing.enabled })}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors
                  ${editing.enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                <Power style={{ width: 15, height: 15 }} />
                {editing.enabled ? t('ads.sqlModeOn') : t('ads.sqlModeOff')}
              </button>
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-xl transition-colors">
                {t('common.cancel')}
              </button>
              <button onClick={save}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md">
                <Save style={{ width: 15, height: 15 }} />
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Editor prompt mode Coding ('code') ──────────────────────────────────────
// TUNGGAL, bukan per-dashboard. Alasannya mengikat: jalur code memakai
// source '__code__' (AICoding.js mengunci activeKey={CODE_KEY}, dan
// aiAsk.needsAutoDetect() mengembalikan false untuk isCode sehingga nilainya
// tak pernah bergeser). Menaruhnya di editor per-dashboard akan menyimpan
// source='solman'/'aws_cost' dst yang TIDAK PERNAH dibaca jalur code.
//
// Tidak lewat ai_sql_sources: '__code__' bukan sumber SQL. Menambahkannya ke
// tabel itu akan memunculkannya sebagai kandidat route-question.
const CODE_SOURCE = '__code__';

function CodeTemplateManager({ showToast }) {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [tpl, setTpl]           = useState('');
  const [meta, setMeta]         = useState({ is_override: false, updated_at: null, required: [], default_template: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/ai/prompt-template', { params: { source: CODE_SOURCE, mode: 'code' } });
      setTpl(r.data?.template || '');
      setMeta({
        is_override:      !!r.data?.is_override,
        updated_at:       r.data?.updated_at || null,
        required:         r.data?.required || [],
        default_template: r.data?.default_template || '',
      });
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to load the coding prompt.', 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  // Peringatan DI SISI KLIEN untuk placeholder wajib. Server tetap menjadi
  // penegak (400 dari /ai/prompt-template); ini hanya agar Taufan tahu sebelum
  // menekan Save, bukan sesudah.
  const missing = (meta.required || []).filter(k => tpl.trim() && !tpl.includes(`{${k}}`));

  const save = async () => {
    if (missing.length) {
      showToast(`Missing required placeholder: ${missing.map(k => `{${k}}`).join(', ')}`, 'error');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/api/ai/prompt-template', { source: CODE_SOURCE, mode: 'code', template: tpl });
      showToast(r.data?.is_override
        ? `Coding prompt saved (${r.data.length} characters).`
        : 'Override cleared — the built-in default is now in effect.', 'success');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to save the coding prompt.', 'error');
    } finally { setSaving(false); }
  };

  const loadBuiltIn = () => {
    if (!meta.default_template) { showToast('Default template is not available.', 'error'); return; }
    if (tpl.trim() && !window.confirm('Replace the editor content with the built-in default template?')) return;
    setTpl(meta.default_template);
  };

  const clearOverride = async () => {
    if (!window.confirm('Clear the override and fall back to the built-in default? The default is stored in the AI Service source code, so nothing is lost.')) return;
    setTpl('');
    setSaving(true);
    try {
      await api.post('/api/ai/prompt-template', { source: CODE_SOURCE, mode: 'code', template: '' });
      showToast('Override cleared — the built-in default is now in effect.', 'success');
      load();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to clear the override.', 'error');
    } finally { setSaving(false); }
  };

  const chars  = tpl.trim().length;
  const approx = Math.round(chars / 3.7);   // kasar, cukup untuk membandingkan sebelum/sesudah

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <RefreshCw style={{ width: 18, height: 18 }} className="animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="px-4 py-3 bg-brand-50 border border-brand-100 rounded-xl text-xs text-brand-700">
        System prompt for <strong>AI Coding</strong> (mode <code className="font-mono">code</code>,
        source <code className="font-mono">{CODE_SOURCE}</code>). This prompt is sent on every turn and
        is billed again on each tool iteration, so its length has a direct cost impact.
        Empty = use the built-in default from the AI Service source code.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border
          ${meta.is_override
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
          {meta.is_override ? 'Override active' : 'Using built-in default'}
        </span>
        {meta.updated_at && (
          <span className="text-xs text-slate-400">Last updated: {meta.updated_at}</span>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          {chars} characters ≈ {approx} tokens
        </span>
      </div>

      <textarea value={tpl} onChange={e => setTpl(e.target.value)} rows={18}
        placeholder={'Leave empty to use the built-in template. If filled, it MUST contain the placeholder {lang}.'}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:border-brand-400 outline-none resize-y font-mono" />

      {missing.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
          <AlertTriangle style={{ width: 14, height: 14 }} className="mt-0.5 shrink-0" />
          <span>
            Missing required placeholder <strong>{missing.map(k => `{${k}}`).join(', ')}</strong>.
            The AI Service will reject this template and keep using the default.
          </span>
        </div>
      )}

      <p className="text-[11px] text-slate-400">
        Keep rule 6b (the filename after the fence tag) intact — it is what turns a complete code block
        into a download button in the UI. Removing it disables that feature with no error message.
      </p>

      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving || missing.length > 0}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-all shadow-md">
          <Save style={{ width: 15, height: 15 }} />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={loadBuiltIn} disabled={saving}
          className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all">
          <Eye style={{ width: 13, height: 13 }} />
          Load built-in default
        </button>
        <button onClick={clearOverride} disabled={saving || !meta.is_override}
          className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 disabled:opacity-50 rounded-xl transition-all">
          <Trash2 style={{ width: 13, height: 13 }} />
          Clear override
        </button>
        <button onClick={load} disabled={saving}
          className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all">
          <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
          Reload
        </button>
      </div>
    </div>
  );
}

export default function AIDataSources() {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(() => {
    const p = searchParams.get('tab');
    return (p === 'queries' || p === 'sql') ? p : 'queries';
  }); // 'queries' | 'sql'
  useTabDeepLink('ai_datasources', (p) => p === 'queries' || p === 'sql', setView);
  const [activeDash, setActiveDash] = useState('solman');
  const [queries, setQueries]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modal, setModal]           = useState(null); // {mode:'add'|'edit', data}
  const [toast, setToast]           = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/ai/context-queries', { params: { dashboard: activeDash } });
      setQueries(res.data.queries || []);
    } catch (e) {
      showToast(e.response?.data?.error || t('ads.errLoadDs'), 'error');
    } finally {
      setLoading(false);
    }
  }, [activeDash]);

  useEffect(() => { fetchQueries(); }, [fetchQueries]);

  const toggleEnabled = async (q) => {
    try {
      await api.put(`/api/ai/context-queries/${q.id}`, { enabled: q.enabled ? 0 : 1 });
      fetchQueries();
    } catch (e) {
      showToast(e.response?.data?.error || t('ads.errToggle'), 'error');
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(t('ads.confirmDeleteDs', { label: q.label }))) return;
    try {
      await api.delete(`/api/ai/context-queries/${q.id}`);
      showToast(t('ads.dsDeleted'));
      fetchQueries();
    } catch (e) {
      showToast(e.response?.data?.error || t('ads.errRemove'), 'error');
    }
  };

  const activeMeta = DASHBOARDS.find(d => d.key === activeDash);

  return (
    <div className="p-6 content-animate">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.type === 'success'
            ? <CheckCircle2 style={{ width: 16, height: 16 }} />
            : <AlertTriangle style={{ width: 16, height: 16 }} />}
          {toast.msg}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display font-bold text-slate-800 text-xl flex items-center gap-2">
            <Database style={{ width: 22, height: 22 }} className="text-brand-600" />
            {t('ads.title')}
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {t('ads.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          {view === 'queries' && (<>
            <button onClick={fetchQueries} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all">
              <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
              {t('common.refresh')}
            </button>
            <button onClick={() => setModal({ mode: 'add' })}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md">
              <Plus style={{ width: 15, height: 15 }} />
              {t('ads.addDs')}
            </button>
          </>)}
        </div>
      </div>

      {/* Switch tampilan: Context Queries (statis) vs Mode SQL (dinamis) */}
      <div className="flex gap-2 mb-5">
        {[['queries', t('ads.tabQueries')], ['sql', t('ads.tabSql')], ['code', 'Coding Prompt']].map(([k, lbl]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-3.5 py-2 rounded-xl text-sm font-semibold transition-all border
              ${view === k ? 'bg-slate-800 text-white border-slate-800 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {view === 'sql'  && (
        <div className="space-y-8">
          <SqlSourcesManager showToast={showToast} />
          <div>
            <h3 className="font-display font-bold text-slate-700 text-sm mb-3 flex items-center gap-2">
              <Database style={{ width: 15, height: 15 }} /> PostgreSQL · Management Reports
            </h3>
            <SqlSourcesManager showToast={showToast} pg />
          </div>
        </div>
      )}
      {view === 'code' && <CodeTemplateManager showToast={showToast} />}

      {view === 'queries' && (<>
      {/* Dashboard tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {DASHBOARDS.map(d => (
          <button key={d.key} onClick={() => setActiveDash(d.key)}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all border
              ${activeDash === d.key
                ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
            {d.label}
          </button>
        ))}
      </div>

      <div className="mb-4 px-4 py-3 bg-brand-50 border border-brand-100 rounded-xl text-xs text-brand-700">
        {t('ads.dsInfoPre')} <strong>{activeMeta?.label}</strong> {t('ads.dsInfoMid')} <code className="font-mono">{activeMeta?.table}</code>.
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
          <RefreshCw style={{ width: 18, height: 18 }} className="animate-spin" />
          <span className="text-sm">{t('common.loading')}</span>
        </div>
      ) : queries.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Database style={{ width: 32, height: 32 }} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t('ads.noDs')}</p>
          <p className="text-xs mt-1">{t('ads.noDsHint')}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {queries.map(q => (
            <div key={q.id}
              className={`border rounded-xl px-4 py-3 transition-all
                ${q.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-slate-800 truncate">{q.label}</span>
                    {q.enabled
                      ? <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs font-medium">{t('ads.active')}</span>
                      : <span className="px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded text-xs font-medium">{t('ads.inactive')}</span>}
                    {q.bot_key && (
                      <span className="px-1.5 py-0.5 bg-sky-50 text-sky-600 rounded text-xs font-medium">
                        ✈️ {q.bot_key}
                      </span>
                    )}
                  </div>
                  <code className="block text-xs font-mono text-slate-500 bg-slate-50 rounded-lg px-2.5 py-1.5 overflow-x-auto whitespace-pre">
                    {q.sql_query}
                  </code>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleEnabled(q)} title={q.enabled ? t('ads.disable') : t('ads.enable')}
                    className={`p-1.5 rounded-lg transition-colors ${q.enabled ? 'text-emerald-500 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}>
                    <Power style={{ width: 15, height: 15 }} />
                  </button>
                  <button onClick={() => setModal({ mode: 'edit', data: q })} title={t('common.edit')}
                    className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
                    <Edit2 style={{ width: 15, height: 15 }} />
                  </button>
                  <button onClick={() => handleDelete(q)} title={t('common.delete')}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 style={{ width: 15, height: 15 }} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}

      {modal && (
        <QueryEditor
          dashboardKey={activeDash}
          initial={modal.mode === 'edit' ? modal.data : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchQueries(); showToast(modal.mode === 'edit' ? t('ads.dsUpdated') : t('ads.dsAdded')); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}
